import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, Copy, CheckCircle, AlertTriangle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-screen MFA flow shown right after password login for admin accounts.
 *
 * Modes received from /api/auth/login:
 *   { mfa_setup_required: true,  setup_token, email }   → enrolment screen
 *   { mfa_required: true,        challenge_token, email } → challenge screen
 *
 * Calls `onSuccess({ access_token, user })` once the gate is fully cleared,
 * or `onCancel()` if the operator wants to back out. The parent (AuthPage)
 * is responsible for actually persisting the token.
 */
export default function MfaFlow({ mode, setupToken, challengeToken, email, onSuccess, onCancel }) {
  // ─── shared state ───────────────────────────────────────────────
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // ─── setup-only state ───────────────────────────────────────────
  const [setupDto, setSetupDto] = useState(null);   // {secret, qr_data_url, provisioning_uri}
  const [setupCode, setSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null); // shown ONCE after verify-setup
  const [savedAcknowledged, setSavedAcknowledged] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(null); // {access_token, user}

  // ─── challenge-only state ───────────────────────────────────────
  const [challengeCode, setChallengeCode] = useState('');

  const beginSetup = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/setup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_token: setupToken }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setSetupDto(data);
    } catch (e) {
      setError(e.message || 'Could not start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const submitSetupCode = async (e) => {
    e?.preventDefault?.();
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/verify-setup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setup_token: setupToken, code: setupCode.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setBackupCodes(data.backup_codes || []);
      setPendingFinish({ access_token: data.access_token, user: data.user });
    } catch (e) {
      setError(e.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const submitChallenge = async (e) => {
    e?.preventDefault?.();
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/mfa/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken, code: challengeCode.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      onSuccess({ access_token: data.access_token, user: data.user, via: data.via });
    } catch (e) {
      setError(e.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const copyCodes = () => {
    if (!backupCodes) return;
    const text = backupCodes.join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success('Backup codes copied'),
      () => toast.error('Copy failed — please save them manually'),
    );
  };

  // ─── render ─────────────────────────────────────────────────────

  // After verify-setup, force the user to acknowledge they saved
  // their backup codes BEFORE we hand them an access token. This
  // is the entire point of the one-time display.
  if (backupCodes && pendingFinish) {
    return (
      <Card className="p-6 w-full max-w-md mx-auto space-y-4" data-testid="mfa-backup-codes">
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle className="w-5 h-5" />
          <h2 className="text-lg font-semibold">MFA enabled — save your backup codes</h2>
        </div>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          These 10 one-time codes can sign you in if you lose your authenticator.
          We never show them again. Store them in a password manager NOW.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border rounded p-3">
          {backupCodes.map((c, i) => (
            <div key={i} className="select-all" data-testid={`backup-code-${i}`}>{c}</div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyCodes} data-testid="copy-backup-codes">
            <Copy className="w-4 h-4 mr-1" /> Copy all
          </Button>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={savedAcknowledged}
            onChange={(e) => setSavedAcknowledged(e.target.checked)}
            data-testid="acknowledge-saved"
          />
          <span>I saved these codes in a secure place.</span>
        </label>
        <Button
          className="w-full"
          disabled={!savedAcknowledged}
          onClick={() => onSuccess(pendingFinish)}
          data-testid="continue-after-backup-codes"
        >
          Continue to admin panel
        </Button>
      </Card>
    );
  }

  if (mode === 'setup') {
    return (
      <Card className="p-6 w-full max-w-md mx-auto space-y-4" data-testid="mfa-setup">
        <div className="flex items-center gap-2 text-slate-800">
          <Shield className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Set up two-factor authentication</h2>
        </div>
        <p className="text-sm text-slate-600">
          Admin accounts must use a TOTP authenticator (Google Authenticator,
          Authy, 1Password, etc.) before they can sign in. Account:{' '}
          <strong>{email}</strong>
        </p>

        {!setupDto && (
          <Button className="w-full" onClick={beginSetup} disabled={loading} data-testid="begin-mfa-setup">
            {loading ? 'Loading…' : 'Begin setup'}
          </Button>
        )}

        {setupDto && (
          <>
            <div className="flex flex-col items-center gap-2">
              <img
                src={setupDto.qr_data_url}
                alt="MFA QR"
                className="w-44 h-44 border rounded bg-white"
                data-testid="mfa-qr"
              />
              <code
                className="text-xs bg-slate-50 border rounded px-2 py-1 select-all break-all"
                data-testid="mfa-secret"
              >
                {setupDto.secret}
              </code>
              <span className="text-[11px] text-slate-500">
                Scan the QR code, or enter the secret manually.
              </span>
            </div>
            <form onSubmit={submitSetupCode} className="space-y-3">
              <Label htmlFor="setup-code">Enter the 6-digit code from your app</Label>
              <Input
                id="setup-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                data-testid="mfa-setup-code"
                required
              />
              {error && (
                <div className="text-sm text-rose-600 flex items-center gap-1" data-testid="mfa-error">
                  <AlertTriangle className="w-4 h-4" /> {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={loading || setupCode.length !== 6}
                data-testid="submit-mfa-setup"
              >
                {loading ? 'Verifying…' : 'Verify and enable MFA'}
              </Button>
            </form>
          </>
        )}

        <Button variant="ghost" className="w-full" onClick={onCancel} data-testid="cancel-mfa">
          Cancel and back to login
        </Button>
      </Card>
    );
  }

  // mode === 'challenge'
  return (
    <Card className="p-6 w-full max-w-md mx-auto space-y-4" data-testid="mfa-challenge">
      <div className="flex items-center gap-2 text-slate-800">
        <KeyRound className="w-5 h-5" />
        <h2 className="text-lg font-semibold">Enter your authenticator code</h2>
      </div>
      <p className="text-sm text-slate-600">
        Account: <strong>{email}</strong>. Use your authenticator's current 6-digit code, or one of your backup codes if you lost the device.
      </p>
      <form onSubmit={submitChallenge} className="space-y-3">
        <Label htmlFor="challenge-code">6-digit code or backup code</Label>
        <Input
          id="challenge-code"
          inputMode="text"
          autoComplete="one-time-code"
          autoFocus
          value={challengeCode}
          onChange={(e) => setChallengeCode(e.target.value)}
          placeholder="123456 or XH5MZZO26Z"
          data-testid="mfa-challenge-code"
          required
        />
        {error && (
          <div className="text-sm text-rose-600 flex items-center gap-1" data-testid="mfa-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={loading || challengeCode.trim().length < 6}
          data-testid="submit-mfa-challenge"
        >
          {loading ? 'Verifying…' : 'Verify'}
        </Button>
      </form>
      <Button variant="ghost" className="w-full" onClick={onCancel} data-testid="cancel-mfa">
        Cancel and back to login
      </Button>
    </Card>
  );
}
